/**
 * Warms the lazy chunk for a /user/* sub-route on sidebar hover so the
 * click-then-navigate sequence skips the chunk-download wait.
 *
 * Specifiers below MUST match App.tsx's `lazy(() => import(...))` strings
 * byte-for-byte — Vite/Rollup keys chunk identity off the specifier.
 *
 * Sidebar-scoped: only top-level /user/<x> entries are registered.
 * Subscription sub-pages (/user/subscription/plan, /manage, /guests,
 * /collaborators, /invoices, /event/:id) are reached via in-page nav
 * from UserSubscriptionPage, not the sidebar, so they live outside this
 * registry.
 */

const REGISTRY: Record<string, () => Promise<unknown>> = {
  settings: () => import("@/pages/user/UserSettingsPage"),
  theme: () => import("@/pages/user/UserThemePage"),
  security: () => import("@/pages/user/UserSecurityPage"),
  company: () => import("@/pages/user/UserCompanyPage"),
  domains: () => import("@/pages/user/UserDomainsPage"),
  "portal-domain": () => import("@/pages/user/CompanyPortalDomainPage"),
  collaborators: () => import("@/pages/user/UserCollaboratorsPage"),
  crm: () => import("@/pages/user/crm/UserCrmPage"),
  privacy: () => import("@/pages/user/privacy/UserPrivacyPage"),
  subscription: () => import("@/pages/user/UserSubscriptionPage"),
};

/**
 * The registered destinations, for the parity test.
 *
 * Adding a `/user/*` page takes three synchronised edits (lazy import, route,
 * this registry) and forgetting THIS one fails silently — the page still loads,
 * just cold. Exposing the keys lets a test assert that every sidebar row has an
 * entry, which is the realistic mistake.
 */
export const USER_ROUTE_PREFETCH_KEYS: readonly string[] = Object.keys(REGISTRY);

export function prefetchUserRoute(url: string): void {
  const match = url.match(/^\/user\/([^/]+)/);
  if (!match) return;
  const loader = REGISTRY[match[1]];
  if (!loader) return;
  loader().catch(() => {});
}

const COMMON_ROUTES = ["settings", "theme", "company"];

export function warmCommonUserRoutes(): void {
  for (const key of COMMON_ROUTES) {
    REGISTRY[key]?.().catch(() => {});
  }
}
